import { Tooltip as TooltipPrimitive } from 'radix-ui'
import type { ReactElement, ReactNode } from 'react'
import { cn } from '../lib/cn'

export const TooltipProvider = TooltipPrimitive.Provider

export interface TooltipProps {
  content: ReactNode
  children: ReactElement
  side?: 'top' | 'right' | 'bottom' | 'left'
  /** Keyboard shortcut shown after the text, e.g. "Ctrl K". */
  shortcut?: string
  disabled?: boolean
  /** Controlled: open it from elsewhere too (e.g. a tap, which has no hover). */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function Tooltip({
  content,
  children,
  side = 'bottom',
  shortcut,
  disabled,
  open,
  onOpenChange,
}: TooltipProps) {
  if (disabled) return children
  return (
    <TooltipPrimitive.Root {...(open === undefined ? {} : { open })} onOpenChange={onOpenChange}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            // A sentence-long tip (a disabled action's reason) wraps instead of running across the screen.
            'z-50 flex max-w-72 items-center gap-2 rounded-panel border border-rule overlay px-2 py-1 text-xs text-ink',
            'animate-fade-in',
          )}
        >
          {content}
          {shortcut ? (
            <kbd dir="ltr" className="font-mono text-2xs text-ink-muted">
              {shortcut}
            </kbd>
          ) : null}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}
